import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SimpleCaseProvider } from './contexts/SimpleCaseContext';
import { AlertProvider } from './contexts/AlertContext';
import { ApiStatusProvider, useApiStatus } from './contexts/ApiStatusContext';
import { MaintenanceProvider, useMaintenance } from './contexts/MaintenanceContext';
import { SiteGateProvider, useSiteGate } from './contexts/SiteGateContext';
import { SiteGatePage } from './pages/SiteGatePage';
import { FeatureFlagProvider, useFeatureFlagContext } from './contexts/FeatureFlagContext';
import { SystemSettingsProvider } from './contexts/SystemSettingsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AlertContainer } from './components/AlertContainer';
import ErrorBoundary from './components/ErrorBoundary';
import { SimpleSpinner } from './components/SimpleSpinner';
import { SpinnerOverlay } from './components/SpinnerOverlay';
import { useVersionCheck } from './hooks/useVersionCheck';
import { StaleBuildReloader } from './components/StaleBuildReloader';
import { AuthDebug, AuthDebugProvider } from './components/AuthDebug';
import { AdminFAB } from './components/AdminFAB';
import { CalculonChatBotFAB } from './components/CalculonChatBotFAB';
import { CalculonChatFabSuppressionProvider } from './contexts/CalculonChatFabSuppressionContext';
import { useAuthContext } from './contexts/AuthContext';
import { setupGlobalErrorHandlers } from './services/errorLoggingService';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { BaseApiService } from './services/BaseApiService';
import { TeamUpdatedModal } from './components/TeamUpdatedModal';
import { ProTrialGate } from './components/ProTrialGate';
import { EnvironmentWatermark } from './components/EnvironmentWatermark';
import { FeatureFlagErrorBanner } from './components/FeatureFlagErrorBanner';
import { PastDueBanner } from './components/PastDueBanner';
import { BillingReadOnlyGate } from './components/BillingReadOnlyGate';
import './App.css';
// Must come after App.css so the responsive overrides win the cascade at equal specificity.
import './styles/responsive.css';

// Set up global error handlers for unhandled promise rejections and errors
setupGlobalErrorHandlers();

// Critical pages - keep in main bundle (needed immediately)
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SignupPage } from './pages/SignupPage';
import { RegisterStartPage } from './pages/RegisterStartPage';
import { RegisterPlanPage } from './pages/RegisterPlanPage';
import { RegisterTeamPage } from './pages/RegisterTeamPage';
import { RegisterPaymentPage } from './pages/RegisterPaymentPage';
import { PaymentReturnPage } from './pages/PaymentReturnPage';
import { RegisterVerifyPage } from './pages/RegisterVerifyPage';
import { RegisterPasswordPage } from './pages/RegisterPasswordPage';
import { RegisterCompletionPage } from './pages/RegisterCompletionPage';
import { RegisterCompletePage } from './pages/RegisterCompletePage';
import { RegisterTeamCompletePage } from './pages/RegisterTeamCompletePage';
import { SignupSessionProvider } from './contexts/SignupSessionContext';
import { ApplicantTeamInvitePage } from './pages/ApplicantTeamInvitePage';
import { ApplicantTeamInvitePasswordPage } from './pages/ApplicantTeamInvitePasswordPage';
import { ResendTeamInvitePage } from './pages/ResendTeamInvitePage';
import { TeamSwitchInvitePage } from './pages/TeamSwitchInvitePage';
import { AcceptTermsPage } from './pages/AcceptTermsPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { HomePage } from './pages/HomePage';
import { TroublePage } from './pages/TroublePage';
import { MaintenancePage } from './pages/MaintenancePage';
import { ClientExpensePage } from './pages/public/ClientExpensePage';

// Lazy load less critical pages - these will be code-split into separate chunks
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const TeamEditPage = lazy(() => import('./pages/TeamEditPage').then(m => ({ default: m.TeamEditPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const UserEditPage = lazy(() => import('./pages/UserEditPage').then(m => ({ default: m.UserEditPage })));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage').then(m => ({ default: m.TermsAndConditionsPage })));
const DownloadsPage = lazy(() => import('./pages/DownloadsPage').then(m => ({ default: m.DownloadsPage })));
const ContinuingEducationPage = lazy(() =>
  import('./pages/ContinuingEducationPage').then(m => ({ default: m.ContinuingEducationPage })),
);
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));

// Lazy load test pages
const IconsTestPage = lazy(() => import('./pages/test/IconsTestPage').then(m => ({ default: m.IconsTestPage })));
const ChevronsTestPage = lazy(() => import('./pages/test/ChevronsTestPage').then(m => ({ default: m.ChevronsTestPage })));
const NavbarTestPage = lazy(() => import('./pages/test/NavbarTestPage').then(m => ({ default: m.NavbarTestPage })));
const PetitionerRespondentTestPage = lazy(() => import('./pages/test/PetitionerRespondentTestPage').then(m => ({ default: m.PetitionerRespondentTestPage })));
const PdfTestPage = lazy(() => import('./pages/test/PdfTestPage').then(m => ({ default: m.PdfTestPage })));
const MonetaryInputTestPage = lazy(() => import('./pages/test/MonetaryInputTestPage').then(m => ({ default: m.MonetaryInputTestPage })));

// Lazy load wizard pages - these are the heaviest and only needed when editing a case
const CaseInfoPage = lazy(() => import('./pages/wizard/IA/CaseInfoPage').then(m => ({ default: m.CaseInfoPage })));
const CustodyPage = lazy(() => import('./pages/wizard/IA/CustodyPage').then(m => ({ default: m.CustodyPage })));
const IncomePage = lazy(() => import('./pages/wizard/IA/IncomePage').then(m => ({ default: m.IncomePage })));
const TaxesPage = lazy(() => import('./pages/wizard/IA/TaxesPage').then(m => ({ default: m.TaxesPage })));
const DeductionsPage = lazy(() => import('./pages/wizard/IA/DeductionsPage').then(m => ({ default: m.DeductionsPage })));
const MedicalPage = lazy(() => import('./pages/wizard/IA/MedicalPage').then(m => ({ default: m.MedicalPage })));
const StepDownPage = lazy(() => import('./pages/wizard/IA/StepDownPage').then(m => ({ default: m.StepDownPage })));
const AlimonyPage = lazy(() => import('./pages/wizard/IA/AlimonyPage').then(m => ({ default: m.AlimonyPage })));
const NetWorthPage = lazy(() => import('./pages/wizard/IA/NetWorthPage').then(m => ({ default: m.NetWorthPage })));
const BudgetPage = lazy(() => import('./pages/wizard/IA/BudgetPage').then(m => ({ default: m.BudgetPage })));
const DivisionPage = lazy(() => import('./pages/wizard/IA/DivisionPage').then(m => ({ default: m.DivisionPage })));

const WizardRedirect: React.FC = () => {
    const { caseId, tabName } = useParams<{ caseId: string; tabName: string }>();
    return <Navigate to={`/wizard/IA/${caseId}/${tabName}`} replace />;
};

/**
 * Component that wraps providers that make API calls
 * Only rendered after maintenance check completes
 */
const AppWithProviders: React.FC = () => {
  const { apiStatus, isMaintenance: isApiMaintenance } = useApiStatus();

  // Check for new application versions and auto-reload if detected
  // Checks every 30 seconds (only in production)
  useVersionCheck(30000, true);

  return (
    <>
      {isApiMaintenance ? (
        <MaintenancePage />
      ) : apiStatus === 'down' ? (
        <TroublePage />
      ) : (
        <AppContent />
      )}
    </>
  );
};

/**
 * Component that shows logout spinner overlay
 * Must be inside Router to use useLocation
 */
const LogoutSpinner: React.FC = () => {
  const authContext = useAuthContext();
  const isLoggingOut = authContext.isLoggingOut ?? false;

  return <SpinnerOverlay isVisible={isLoggingOut} message="Logging out..." />;
};

/**
 * Component that shows warming up spinner overlay during initial auth check
 * Must be inside AuthProvider to access auth context
 */
const WarmingUpSpinner: React.FC = () => {
  const authContext = useAuthContext();
  const isLoading = authContext.loading ?? false;
  const [showSpinner, setShowSpinner] = React.useState(false);
  const [hasShownOnce, setHasShownOnce] = React.useState(false);

  // Track when loading starts and ensure spinner shows for minimum duration
  React.useEffect(() => {
    if (isLoading && !hasShownOnce) {
      // Show spinner immediately when loading starts
      setShowSpinner(true);
      setHasShownOnce(true);
    } else if (!isLoading && showSpinner) {
      // Keep spinner visible for at least 500ms even after loading completes
      // This ensures users see it even on fast connections
      const timer = setTimeout(() => {
        setShowSpinner(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, showSpinner, hasShownOnce]);

  // Show spinner whenever auth is loading or during minimum display period
  return <SpinnerOverlay isVisible={showSpinner || isLoading} message="Warming up..." />;
};

/**
 * Inner app component that renders the router and routes
 */
const AppContent: React.FC = () => {
  const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
  const notificationsEnabled = flags['Notifications'] ?? false;
  const continuingEducationEnabled = flags['ContinuingEducation'] ?? false;
  const { logout } = useAuthContext();
  const [showTeamUpdatedModal, setShowTeamUpdatedModal] = React.useState(false);

  React.useEffect(() => {
    BaseApiService.setOnTeamUpdateRevocationHandler(() => setShowTeamUpdatedModal(true));
  }, []);

  const handleTeamUpdatedLogout = React.useCallback(async () => {
    setShowTeamUpdatedModal(false);
    await logout();
  }, [logout]);

  return (
    <Router>
      <CalculonChatFabSuppressionProvider>
        <div className="app">
          <EnvironmentWatermark />
          <FeatureFlagErrorBanner />
          <PastDueBanner />
          <BillingReadOnlyGate />
          <StaleBuildReloader />
          <AlertContainer />
          <LogoutSpinner />
          <TeamUpdatedModal isOpen={showTeamUpdatedModal} onLogout={handleTeamUpdatedLogout} />
          <ProTrialGate />
          <AdminFAB />
          <CalculonChatBotFAB />
          <Routes>
          {/* Public routes */}
          <Route
            path="/auth/login"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <LoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <ResetPasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/signup"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterStartPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/plan"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterPlanPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/team"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterTeamPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/payment"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterPaymentPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/verify"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterVerifyPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/password"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterPasswordPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/done"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterCompletionPage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/complete"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <RegisterCompletePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/register/team-complete"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <SignupSessionProvider>
                  <RegisterTeamCompletePage />
                </SignupSessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/complete-team-invite"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <ApplicantTeamInvitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/complete-team-invite/password"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <ApplicantTeamInvitePasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/join-team"
            element={
              <ProtectedRoute requireAuth={true}>
                <TeamSwitchInvitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/resend-invite"
            element={
              <ProtectedRoute requireAuth={true}>
                <ResendTeamInvitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/accept-terms"
            element={
              <ProtectedRoute requireAuth={true}>
                <AcceptTermsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/unsubscribe"
            element={
              <ProtectedRoute requireAuth={false} redirectTo="/auth/login">
                <UnsubscribePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/expenses/t/:token"
            element={
              <ClientExpensePage />
            }
          />
          <Route
            path="/client/expenses"
            element={
              <ClientExpensePage />
            }
          />

          {/* Protected routes */}
          <Route
            path="/cases/list"
            element={
              <ProtectedRoute requireAuth={true}>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/received"
            element={
              <ProtectedRoute requireAuth={true}>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/sent"
            element={
              <ProtectedRoute requireAuth={true}>
                <HomePage />
              </ProtectedRoute>
            }
          />
          {/* Redirect root to cases/list for backward compatibility */}
          <Route
            path="/"
            element={
              <ProtectedRoute requireAuth={true}>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading admin..." />}>
                  <AdminPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading settings..." />}>
                  <SettingsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Stripe's 3D Secure redirect return. Not wrapped in ProtectedRoute: a signup return has
              no login yet; the page applies ProtectedRoute itself for everything else. */}
          <Route path="/billing/payment-return" element={<PaymentReturnPage />} />
          <Route
            path="/downloads"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading downloads..." />}>
                  <DownloadsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {notificationsEnabled && (
            <Route
              path="/notifications"
              element={
                <ProtectedRoute requireAuth={true}>
                  <Suspense fallback={<SimpleSpinner message="Loading notifications..." />}>
                    <NotificationsPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          )}
          {continuingEducationEnabled && (
            <Route
              path="/continuing-education"
              element={
                <ProtectedRoute requireAuth={true}>
                  <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                    <ContinuingEducationPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/terms_and_conditions"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <TermsAndConditionsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test/icons"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <IconsTestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test/chevrons"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <ChevronsTestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test/navbar"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <NavbarTestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test/pdf"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <PdfTestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test/monetary-input"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <MonetaryInputTestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test/petitionerRespondent"
            element={
              <ProtectedRoute requireAuth={true}>
                <Suspense fallback={<SimpleSpinner message="Loading..." />}>
                  <PetitionerRespondentTestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Redirect legacy /wizard/:caseId/:tabName → /wizard/IA/:caseId/:tabName */}
          <Route path="/wizard/:caseId/:tabName" element={<WizardRedirect />} />

          {/* Wizard routes - lazy loaded for code splitting */}
          <Route
            path="/wizard/:stateCode/:caseId/case"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading case information..." />}>
                      <CaseInfoPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/custody"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading custody information..." />}>
                      <CustodyPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/income"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading income information..." />}>
                      <IncomePage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/taxes"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading tax information..." />}>
                      <TaxesPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/deductions"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading deductions..." />}>
                      <DeductionsPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/medical"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading medical information..." />}>
                      <MedicalPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/stepdown"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading step down information..." />}>
                      <StepDownPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/alimony"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading alimony analysis..." />}>
                      <AlimonyPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/networth"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading net worth information..." />}>
                      <NetWorthPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/budget"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading budget information..." />}>
                      <BudgetPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard/:stateCode/:caseId/division"
            element={
              <ProtectedRoute requireAuth={true}>
                <SimpleCaseProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<SimpleSpinner message="Loading division information..." />}>
                      <DivisionPage />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleCaseProvider>
              </ProtectedRoute>
            }
          />

          {/* Catch all route - only after flags loaded to avoid redirecting feature-flagged routes */}
          {!flagsLoading && <Route path="*" element={<Navigate to="/" replace />} />}
          </Routes>
        </div>
      </CalculonChatFabSuppressionProvider>
    </Router>
  );
};

/**
 * Top-level component that checks maintenance before initializing API-dependent providers
 */
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <MaintenanceProvider>
          <MaintenanceGate />
        </MaintenanceProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * Component that gates API-dependent providers behind maintenance check
 * This ensures maintenance mode is checked BEFORE any API calls are made
 */
const MaintenanceGate: React.FC = () => {
  const { isMaintenance: isMaintenanceMode, isLoading: isMaintenanceLoading } = useMaintenance();
  const [hasCheckedMaintenance, setHasCheckedMaintenance] = React.useState(false);

  // Track when maintenance check completes
  React.useEffect(() => {
    if (!isMaintenanceLoading) {
      // Use a small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        setHasCheckedMaintenance(true);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isMaintenanceLoading]);

  // Show loading spinner while checking maintenance mode
  // CRITICAL: Don't render anything else until check completes
  if (isMaintenanceLoading || !hasCheckedMaintenance) {
    return <SimpleSpinner message="Checking system status..." />;
  }

  // Show maintenance page if external maintenance mode is enabled
  // This happens BEFORE any API-dependent providers are mounted
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  // Only mount API-dependent providers after maintenance check passes AND the sitewide gate is
  // satisfied. The gate (test/cert only) is checked BEFORE AuthProvider mounts so that AuthProvider's
  // /auth/me call is never made against a locked environment.
  return (
    <SiteGateProvider>
      <SiteGateGate>
        <ApiStatusProvider>
          <AuthProvider>
            <FeatureFlagProvider>
              <SystemSettingsProvider>
                <WarmingUpSpinner />
                <AuthDebugProvider>
                  <AlertProvider>
                    <AppWithProviders />
                  </AlertProvider>
                </AuthDebugProvider>
              </SystemSettingsProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        </ApiStatusProvider>
      </SiteGateGate>
    </SiteGateProvider>
  );
};

/**
 * Gates API-dependent providers behind the sitewide password (test/cert only). Inert in prod/local.
 */
const SiteGateGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { required, unlocked, isLoading } = useSiteGate();

  if (isLoading) {
    return <SimpleSpinner message="Checking system status..." />;
  }

  if (required && !unlocked) {
    return <SiteGatePage />;
  }

  return <>{children}</>;
};

export default App;
